import * as React from 'react';

import { PageHeader } from '../core/PageHeader';
import { useCallback, useEffect, useMemo } from 'react';
import { onPageDidMount, onPageWillUnmount } from '../../redux/core';
import { useDispatch, useSelector } from 'react-redux';
import { IconButton, IContextualMenuItem, Pivot, PivotItem } from '@fluentui/react';
import { RootState } from '../../redux/types';
import { useNavigate, useParams } from 'react-router-dom';
import { MarketCapacity } from './MarketCapacity';
import { MarketServices } from './MarketServices';
import { MarketPayers } from './MarketPayers';
import { useQueryString } from '../../util/hooks';
import { Market } from '../../api/markets.types';
import { ROUTES } from '../../config/routes';
import _ from 'lodash';

export const MarketDetails = () => {
  const dispatch = useDispatch();
  const optionKeyDepressed = useSelector((state: RootState) => state.ui.optionKeyDepressed);
  const [tab, setTab] = useQueryString('tab', 'capacity');
  const { code } = useParams<{ code: string }>();
  const markets = useSelector((state: RootState) => state.markets.allMarkets);

  useEffect(() => {
    dispatch(onPageDidMount('MarketDetails'));
    return () => {
      dispatch(onPageWillUnmount('MarketDetails'));
    };
  }, [dispatch]);

  return (
    <>
      <PageHeader
        title={<MarketSwitcher tab={tab} code={code!} markets={markets.data || []} />}
        pivot={
          <Pivot selectedKey={tab} onLinkClick={(i) => setTab(i?.props.itemKey || 'capacity')}>
            <PivotItem headerText="Capacity" itemKey="capacity" />
            {optionKeyDepressed && <PivotItem headerText="Services" itemKey="services" />}
            {optionKeyDepressed && <PivotItem headerText="Payers" itemKey="payers" />}
          </Pivot>
        }
      />
      {tab === 'capacity' && <MarketCapacity />}
      {tab === 'services' && <MarketServices />}
      {tab === 'payers' && <MarketPayers />}
    </>
  );
};

const MarketSwitcher = ({ code, markets, tab }: { code: string; markets: Market[]; tab: string }) => {
  const market = markets.find((m) => m.code === code);
  const navigate = useNavigate();

  const onClick = useCallback(
    (ev?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, item?: IContextualMenuItem) => {
      if (item?.key) {
        navigate(`${ROUTES.markets}/${item.key}?tab=${tab}`, { replace: true });
      }
    },
    // Intentional to not have history in dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tab]
  );

  const allOptions = useMemo(() => {
    const options: any[] = [];

    const regions = _.groupBy(
      markets.filter((m) => m.active),
      'region'
    );
    _.keys(regions).forEach((region) => {
      const regionMarkets: Market[] = regions[region];

      const groups = _.groupBy(regionMarkets, 'groupCode');
      for (let group in groups) {
        groups[group].forEach((m) => {
          options.push({
            key: m.code,
            text: `${region} - ${group} - ${m.name} (${m.code})`,
            onClick: onClick,
          });
        });
      }
    });

    return options;
  }, [markets, onClick]);

  return (
    <>
      {`${market?.region} - ${market?.groupCode} - ${market?.name} (${market?.code})`}
      <IconButton menuProps={{ items: allOptions, calloutProps: { calloutWidth: 400 } }} />
    </>
  );
};
